import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: {
      main: '#8a2be2', // Purple
    },
    secondary: {
      main: '#9370db', // Medium Purple
    },
    background: {
      default: '#f0e6ff', // Light Purple
    },
  },
  typography: {
    fontFamily: "'Brush Script MT', cursive",
    secondaryFontFamily: "'Brush Script MT', 'Comic Sans MS', 'Bradley Hand', cursive",
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      color: 'white',
      textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
    },
    h2: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      color: 'primary.main',
    },
    body1: {
      color: 'primary.main',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'primary.main',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(45deg, secondary.main, primary.main)',
          color: 'white',
          '&:hover': {
            backgroundImage: 'linear-gradient(45deg, primary.main, secondary.main)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: 'secondary.main',
          },
          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'primary.main',
          '&.Mui-focused': {
            color: 'secondary.main',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: 'background.default',
        },
        icon: {
          color: 'secondary.main',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: 'primary.main',
          backgroundColor: 'background.default',
          '&:hover': {
            backgroundColor: 'rgba(147, 112, 219, 0.2)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(147, 112, 219, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(147, 112, 219, 0.4)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'background.default',
          borderRadius: 16,
          padding: 16,
          position: 'relative',
        },
      },
    },
  },
});


export const styles = {
  title: {
    flexGrow: 1,
    textAlign: 'center',
    py: 2,
    fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3.5rem' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  bookNowLink: {
    display: 'inline-block',
    minWidth: 200,
  },
  bookNowButton: {
    width: '100%',
    backgroundImage: 'linear-gradient(45deg, primary.main, secondary.main)',
  },
  imageBox: {
    width: { xs: 'calc(33.333% - 8px)', sm: 'calc(33.333% - 10.667px)' },
    aspectRatio: '1/1',
  },
  imageCard: {
    height: '100%',
  },
  cardMedia: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  calendarPaper: {
    p: 0,
    borderRadius: 0,
    background: 'linear-gradient(to bottom right, #fff0f5, #fff5ee)',
    boxShadow: 'none',
  },
  dateCalendar: {
    '& .MuiPickersDay-root': {
      color: 'primary.main',
      '&.Mui-selected': {
        backgroundColor: 'secondary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.main',
        },
      },
    },
    '& .MuiPickersCalendarHeader-root': {
      paddingTop: 0,
    }
  },
  timeSelectionContainer: {
    position: 'relative',
    height: 198,
    mb: 2,
  },
  timeSelectionGradient: {
    position: 'absolute', 
    left: 0, 
    right: 0, 
    textAlign: 'center', 
    zIndex: 1,
    pointerEvents: 'none',
    height: '30px',
  },
  timeSelectionGradientTop: {
    top: 0,
    background: 'linear-gradient(to bottom, background.default 0%, rgba(255,255,255,0) 100%)',
  },
  timeSelectionGradientBottom: {
    bottom: 0,
    background: 'linear-gradient(to top, background.default 0%, rgba(255,255,255,0) 100%)',
  },
  timeSelectionScroll: {
    height: '100%', 
    overflowY: 'auto', 
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': { background: 'background.default' },
    '&::-webkit-scrollbar-thumb': { background: 'secondary.main', borderRadius: '4px' },
    '&::-webkit-scrollbar-thumb:hover': { background: 'primary.main' },
    scrollSnapType: 'y mandatory',
  },
  timeToggleButtonGroup: {
    width: '100%',
  },
  timeToggleButton: {
    justifyContent: 'center',
    width: '100%',
    height: '66px',
    color: 'primary.main',
    scrollSnapAlign: 'start',
    '&.Mui-selected': {
      backgroundColor: 'secondary.main',
      color: 'white',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
  selectedTimeContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(240, 230, 255, 0.9)',
    boxShadow: '0px -2px 4px rgba(0,0,0,0.1)',
    padding: 2,
    zIndex: 1000,
  },
  selectedTimeLabel: {
    fontWeight: 'bold',
    marginBottom: 1,
  },
  selectedTime: {
    color: 'primary.main',
    marginBottom: 1,
  },
  bookButton: {
    mt: 1,
    fontSize: '1.2rem',
    width: '100%',
    '&:disabled': {
      backgroundImage: 'linear-gradient(45deg, #d8bfd8, #dda0dd)',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  welcomeMessage: {
    marginBottom: 1,
    fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
  },
  welcomeDescription: {
    color: 'primary.main',
    marginBottom: 2,
    fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
    fontFamily: theme.typography.secondaryFontFamily,
  },
  pricesTitle: {
    marginBottom: 1,
    // fontFamily: theme.typography.secondaryFontFamily,
  },
  priceItem: {
    // color: '#ff1493',
    fontWeight: 'bold',
    marginBottom: 0.5,
    fontFamily: theme.typography.secondaryFontFamily,
  },
  bookPopupButton: {
    width: '100%',
    marginBottom: 2,
    padding: '12px',
    fontSize: '1.2rem',
    fontFamily: theme.typography.secondaryFontFamily,
  },
  bookPopupText: {
    textAlign: 'center',
    marginBottom: 5,
    fontSize: '1.3rem',
    fontFamily: theme.typography.secondaryFontFamily,
  },
  bookPopupCloseButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: (theme) => theme.palette.grey[500],
  },
  imagePopup: {
    dialog: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      margin: '16px',
    },
    dialogContent: {
      padding: 0,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      right: 8,
      top: 8,
      color: 'white', // Changed back to white
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    closeIcon: {
      fontSize: 32,
      filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.5))',
    },
    cardMedia: {
      width: '100%',
      height: '100%',
      maxHeight: 'calc(100vh - 32px)',
      objectFit: 'contain',
    },
  },
  spanishMessage: {
    fontFamily: theme.typography.secondaryFontFamily,
    fontSize: '0.9rem',
    color: 'primary.main',
    fontStyle: 'italic',
    textShadow: '1px 1px 2px rgba(255,255,255,0.5)',
  },
};
